import { css, html, LitElement, PropertyValues, TemplateResult } from "lit";
import { customElement, property } from "lit/decorators.js";
import { playUnlockSound } from "./unlock-sounds.js";

@customElement("reading-bee-unlock-overlay")
export class ReadingBeeUnlockOverlay extends LitElement {
  static override styles = [
    css`
      :host {
        position: fixed;
        inset: 0;
        z-index: 20000;
        display: grid;
        place-items: center;
        overflow: visible;
        background: radial-gradient(900px 520px at 50% 30%, #241c12 0%, #0c0b09 62%);
        color: #f4ead5;
        font-family: Nunito, "Avenir Next", "Segoe UI", sans-serif;
      }

      :host([leaving]) {
        animation: fadeOut 420ms ease forwards;
      }

      .stage {
        display: grid;
        justify-items: center;
        gap: 1.1rem;
        padding: 1.5rem;
      }

      .lock {
        width: 108px;
        height: 144px;
        overflow: visible;
        color: #e8b84a;
      }

      .shackle {
        transform-box: fill-box;
        transform-origin: center bottom;
      }

      :host([animated]) .shackle {
        animation: unlatch 900ms cubic-bezier(0.16, 1, 0.3, 1) 120ms forwards;
      }

      .glow {
        width: 168px;
        height: 168px;
        position: absolute;
        border-radius: 50%;
        background: radial-gradient(circle, rgba(232, 184, 74, 0.28) 0%, transparent 68%);
      }

      :host([animated]) .glow {
        animation: pulse 1.4s ease-in-out infinite;
      }

      .lock-wrap {
        position: relative;
        display: grid;
        place-items: center;
        overflow: visible;
        padding-top: 0.4rem;
        filter: drop-shadow(0 0 18px rgba(232, 184, 74, 0.45));
      }

      h1 {
        margin: 0;
        font-size: 1.55rem;
        letter-spacing: -0.03em;
      }

      p {
        margin: 0;
        color: #8a8175;
        font-size: 0.95rem;
      }

      @keyframes unlatch {
        to {
          transform: translateY(-22px);
        }
      }

      @keyframes pulse {
        0%,
        100% {
          transform: scale(0.92);
          opacity: 0.65;
        }
        50% {
          transform: scale(1.12);
          opacity: 1;
        }
      }

      @keyframes fadeOut {
        to {
          opacity: 0;
        }
      }
    `,
  ];

  @property({ type: Boolean, reflect: true }) leaving = false;
  @property({ type: Boolean, reflect: true }) animated = false;
  private playedSound = false;

  override connectedCallback(): void {
    super.connectedCallback();
    this.maybePlaySound();
  }

  protected override updated(changed: PropertyValues<this>): void {
    if (changed.has("animated")) {
      this.maybePlaySound();
    }
  }

  override render(): TemplateResult {
    return html`
      <div class="stage">
        <div class="lock-wrap">
          <div class="glow" aria-hidden="true"></div>
          <svg class="lock" viewBox="0 -8 24 32" fill="none" aria-hidden="true">
            <path
              class="shackle"
              d="M8 11V8a4 4 0 0 1 8 0v3"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round" />
            <rect x="5" y="11" width="14" height="10" rx="2" stroke="currentColor" stroke-width="1.8" />
            <circle cx="12" cy="16" r="1.15" fill="currentColor" />
          </svg>
        </div>
        <h1>Unlocking</h1>
        <p>Checking your instructor account</p>
      </div>
    `;
  }

  private maybePlaySound(): void {
    if (!this.animated || this.playedSound) return;
    this.playedSound = true;
    playUnlockSound();
  }
}
